/**
 * Sound recipes (DESIGN 6.8), one function per thing that can happen.
 * Pure wiring onto the Synth: every patch is a few oscillators / filtered
 * noise bursts with explicit numbers, so a sound can be tuned without
 * touching the event plumbing in sfx.ts.
 *
 * Element tones are deliberately far apart in timbre and pitch (sine
 * chirp, triangle, square, sine+hiss, saw buzz, low saw, noise thud, noise
 * crackle) so a player hears which element landed in the queue without
 * looking, and a combine always reads as "two tones gliding into one".
 */

import type { DamageType, Element, GameEvent, MagickId, RoomPhase, SpellKind, StatusId } from '@shared/index.js';
import { dominant } from '@shared/index.js';
import type { Synth } from './synth.js';

/** Base pitch per element (DESIGN 6.8 element presses); beams, novas and chimes are built on it. */
export const ELEMENT_FREQ: Readonly<Record<Element, number>> = {
  water: 440, life: 660, shield: 330, cold: 880, lightning: 220, arcane: 110, earth: 90, fire: 200, steam: 500, ice: 990, mud: 70, lava: 150, spring: 560,
  plasma: 247, flux: 165, stillness: 784, obsidian: 123, ether: 622, cloud: 523, ash: 175, rime: 1175, moment: 698, dragonfire: 185, stormglass: 277, thunderhead: 98, time: 349,
  tide: 392, pulse: 740, void: 82, wildfire: 233, whirlpool: 466, deluge: 131, inferno: 139, glacier: 104, soul: 880, sun: 587, winter: 1319, death: 73,
};

/** The element that colours a cast's sound: the form-deciding one, arcane for an empty list. */
export function leadElement(els: readonly Element[]): Element {
  return dominant(els) ?? 'arcane';
}

/** Two tones gliding into one (DESIGN 6.8 "combine"). */
function combine(s: Synth, a: number, b: number, to: number, pan: number): void {
  s.tone({ freq: a, glideTo: to, glideTime: 0.12, dur: 0.22, type: 'sine', gain: 0.18, pan, release: 0.08 });
  s.tone({ freq: b, glideTo: to, glideTime: 0.12, dur: 0.22, type: 'triangle', gain: 0.14, pan, release: 0.08 });
}

export function elementPress(s: Synth, el: Element, pan: number): void {
  switch (el) {
    case 'water':
      s.tone({ freq: 440, glideTo: 660, glideTime: 0.08, dur: 0.16, type: 'sine', gain: 0.25, pan });
      break;
    case 'life':
      s.tone({ freq: 660, dur: 0.2, type: 'triangle', gain: 0.22, pan, release: 0.12 });
      break;
    case 'shield':
      s.tone({ freq: 330, dur: 0.14, type: 'square', gain: 0.1, lowpass: 1500, pan });
      break;
    case 'cold':
      s.tone({ freq: 880, dur: 0.18, type: 'sine', gain: 0.2, pan });
      s.noise({ dur: 0.12, filter: 'highpass', freq: 5000, gain: 0.06, pan });
      break;
    case 'lightning':
      s.tone({ freq: 220, dur: 0.16, type: 'sawtooth', gain: 0.14, tremoloHz: 40, tremoloDepth: 0.8, lowpass: 2500, pan });
      break;
    case 'arcane':
      s.tone({ freq: 110, dur: 0.25, type: 'sawtooth', gain: 0.16, lowpass: 900, pan });
      break;
    case 'earth':
      s.noise({ dur: 0.18, filter: 'lowpass', freq: 220, freqTo: 60, gain: 0.5, pan });
      s.tone({ freq: 90, glideTo: 45, dur: 0.18, type: 'sine', gain: 0.3, pan });
      break;
    case 'fire':
      s.noise({ dur: 0.2, filter: 'bandpass', freq: 400, q: 1.5, gain: 0.3, pan });
      s.tone({ freq: 200, dur: 0.12, type: 'triangle', gain: 0.1, tremoloHz: 25, tremoloDepth: 0.9, pan });
      break;
    case 'steam':
      combine(s, 440, 200, 500, pan);
      s.noise({ dur: 0.25, filter: 'bandpass', freq: 2500, q: 0.8, gain: 0.12, pan });
      break;
    case 'ice':
      combine(s, 440, 880, 990, pan);
      s.tone({ freq: 1980, dur: 0.3, type: 'sine', gain: 0.08, pan, release: 0.2 });
      break;
  }
}

/** Steam or Ice falling back to Water: the combine glide in reverse, plus a plop. */
export function degradeBlip(s: Synth, pan: number): void {
  s.tone({ freq: 500, glideTo: 440, glideTime: 0.1, dur: 0.18, type: 'sine', gain: 0.18, pan });
  s.tone({ freq: 700, glideTo: 330, glideTime: 0.14, dur: 0.16, type: 'triangle', gain: 0.1, pan, at: 0.02 });
}

export function cancelBlip(s: Synth, pan: number): void {
  s.tone({ freq: 600, glideTo: 180, dur: 0.12, type: 'square', gain: 0.1, lowpass: 2000, pan });
}

export function fullClick(s: Synth, pan: number): void {
  s.noise({ dur: 0.012, filter: 'highpass', freq: 3000, gain: 0.3, pan, release: 0.01 });
  s.tone({ freq: 1200, dur: 0.02, type: 'square', gain: 0.08, pan });
}

/** The predicted queue was longer than the server's (DESIGN 5.6 "fizzle"). */
export function fizzle(s: Synth, pan: number): void {
  s.noise({ dur: 0.15, filter: 'bandpass', freq: 1500, freqTo: 300, q: 1.2, gain: 0.2, pan });
}

/** Lightning pressed while wet: a harsh buzz nobody mistakes for a normal press (DESIGN 6.7). */
export function selfShockBuzz(s: Synth, pan: number, gain = 1): void {
  s.tone({ freq: 55, dur: 0.4, type: 'sawtooth', gain: 0.25 * gain, tremoloHz: 60, tremoloDepth: 0.9, lowpass: 1200, pan });
  s.tone({ freq: 110, dur: 0.35, type: 'square', gain: 0.12 * gain, tremoloHz: 47, tremoloDepth: 0.9, lowpass: 900, pan });
  s.noise({ dur: 0.25, filter: 'highpass', freq: 2500, gain: 0.2 * gain, pan });
}

// ------------------------------------------------------------------ casts

const SPRAY_FREQ: Readonly<Partial<Record<Element, number>>> = { fire: 500, cold: 3000, water: 1200, steam: 2200, lightning: 2800 };

export function castSound(s: Synth, kind: SpellKind, els: readonly Element[], pan: number): void {
  const lead = leadElement(els);
  const f = ELEMENT_FREQ[lead];
  switch (kind) {
    case 'beam':
      s.tone({ freq: f, glideTo: f * 2, glideTime: 0.1, dur: 0.25, type: 'triangle', gain: 0.2, pan });
      break;
    case 'spray':
      s.noise({ dur: 0.4, filter: 'bandpass', freq: SPRAY_FREQ[lead] ?? 1200, q: 0.9, gain: 0.25, pan, attack: 0.02, release: 0.15 });
      s.tone({ freq: f, dur: 0.1, type: 'sine', gain: 0.1, pan });
      break;
    case 'arc':
      for (let i = 0; i < 4; i++) s.noise({ dur: 0.04, filter: 'highpass', freq: 2500, gain: 0.3, pan, at: i * 0.05 });
      s.tone({ freq: 220, dur: 0.2, type: 'sawtooth', gain: 0.12, tremoloHz: 50, tremoloDepth: 0.9, lowpass: 3000, pan });
      break;
    case 'projectile':
      if (els.includes('earth')) {
        s.noise({ dur: 0.3, filter: 'lowpass', freq: 160, freqTo: 50, gain: 0.6, pan });
        s.tone({ freq: 70, glideTo: 35, dur: 0.3, type: 'sine', gain: 0.4, pan });
        s.noise({ dur: 0.25, filter: 'bandpass', freq: 900, freqTo: 300, q: 0.7, gain: 0.2, pan });
      } else {
        s.noise({ dur: 0.25, filter: 'highpass', freq: 2000, freqTo: 6000, gain: 0.25, pan });
        s.tone({ freq: 1200, glideTo: 1800, dur: 0.12, type: 'sine', gain: 0.12, pan });
      }
      break;
    case 'nova':
      s.chord([f, f * 1.5, f * 2], { dur: 0.4, type: 'triangle', gain: 0.14, pan, release: 0.25, reverb: 0.3 });
      s.noise({ dur: 0.35, filter: 'lowpass', freq: 400, freqTo: 3000, gain: 0.35, pan, attack: 0.03, release: 0.2 });
      break;
    case 'quake':
      s.noise({ dur: 0.7, filter: 'lowpass', freq: 90, gain: 0.7, pan, attack: 0.02, release: 0.3 });
      s.tone({ freq: 40, dur: 0.6, type: 'sine', gain: 0.5, pan, release: 0.3 });
      break;
    case 'barrier':
    case 'bubble':
      s.chord([880, 1320], { dur: 0.45, type: 'sine', gain: 0.14, pan, release: 0.3, reverb: 0.3 });
      break;
    case 'wall':
      s.chord([660, 990], { dur: 0.4, type: 'sine', gain: 0.12, pan, release: 0.3 });
      s.noise({ dur: 0.3, filter: 'lowpass', freq: 200, freqTo: 60, gain: 0.5, pan });
      break;
    case 'mines':
      for (let i = 0; i < 3; i++) s.noise({ dur: 0.02, filter: 'highpass', freq: 4000, gain: 0.3, pan, at: i * 0.1 });
      s.tone({ freq: 1320, dur: 0.05, type: 'square', gain: 0.06, pan, at: 0.2 });
      break;
    case 'storm':
      s.noise({ dur: 0.6, filter: 'lowpass', freq: 300, gain: 0.4, pan, attack: 0.05, release: 0.3 });
      for (let i = 0; i < 3; i++) s.noise({ dur: 0.04, filter: 'highpass', freq: 2500, gain: 0.2, pan, at: 0.1 + i * 0.12 });
      break;
    case 'aura':
      [660, 880, 1320].forEach((freq, i) => s.tone({ freq, dur: 0.35, type: 'sine', gain: 0.12, pan, at: i * 0.07, release: 0.3, reverb: 0.3 }));
      break;
    case 'armor':
      s.noise({ dur: 0.1, filter: 'bandpass', freq: 800, q: 2, gain: 0.35, pan });
      s.tone({ freq: 160, dur: 0.12, type: 'square', gain: 0.1, lowpass: 1200, pan });
      break;
    case 'self':
      s.chord([f * 2, f * 2.5], { dur: 0.3, type: 'sine', gain: 0.12, pan, release: 0.2 });
      break;
    case 'melee':
      s.noise({ dur: 0.15, filter: 'bandpass', freq: 1800, freqTo: 600, q: 0.8, gain: 0.3, pan });
      break;
    case 'wind':
      s.noise({ dur: 0.35, filter: 'lowpass', freq: 300, freqTo: 2500, gain: 0.35, pan, attack: 0.03, release: 0.15 });
      break;
    case 'none':
      break;
  }
}

// ------------------------------------------------------------------ hits, heals, statuses

export function hitSound(s: Synth, type: DamageType, crit: boolean, pan: number): void {
  switch (type) {
    case 'physical':
      s.noise({ dur: 0.06, filter: 'lowpass', freq: 400, gain: 0.25, pan });
      s.tone({ freq: 120, dur: 0.05, type: 'sine', gain: 0.15, pan });
      break;
    case 'fire':
      s.noise({ dur: 0.08, filter: 'bandpass', freq: 600, q: 2, gain: 0.18, pan });
      break;
    case 'cold':
      s.tone({ freq: 1500, dur: 0.05, type: 'sine', gain: 0.1, pan });
      break;
    case 'lightning':
      s.noise({ dur: 0.04, filter: 'highpass', freq: 3000, gain: 0.2, pan });
      s.tone({ freq: 220, dur: 0.04, type: 'sawtooth', gain: 0.08, pan });
      break;
    case 'arcane':
      s.tone({ freq: 110, dur: 0.08, type: 'sawtooth', gain: 0.12, lowpass: 800, pan });
      break;
    case 'steam':
      s.noise({ dur: 0.08, filter: 'bandpass', freq: 2500, q: 1, gain: 0.14, pan });
      break;
    case 'water':
      s.noise({ dur: 0.08, filter: 'bandpass', freq: 1000, q: 1, gain: 0.12, pan });
      s.tone({ freq: 500, glideTo: 400, dur: 0.06, type: 'sine', gain: 0.08, pan });
      break;
    case 'life':
      healSound(s, pan);
      return;
  }
  // Wet x2 and frozen x3 crits get a bright ping so the bonus is heard, not only read.
  if (crit) s.tone({ freq: 1760, dur: 0.08, type: 'sine', gain: 0.1, pan });
}

export function healSound(s: Synth, pan: number): void {
  s.tone({ freq: 660, dur: 0.1, type: 'triangle', gain: 0.1, pan });
  s.tone({ freq: 880, dur: 0.12, type: 'triangle', gain: 0.1, pan, at: 0.07 });
}

export function statusSound(s: Synth, id: StatusId, pan: number): void {
  switch (id) {
    case 'wet':
      s.noise({ dur: 0.2, filter: 'bandpass', freq: 1200, q: 0.7, gain: 0.2, pan });
      s.tone({ freq: 600, glideTo: 300, dur: 0.15, type: 'sine', gain: 0.1, pan });
      break;
    case 'burning':
      for (let i = 0; i < 3; i++) s.noise({ dur: 0.05, filter: 'bandpass', freq: 500, q: 1.5, gain: 0.25, pan, at: i * 0.06 });
      break;
    case 'chilled':
      s.tone({ freq: 2200, dur: 0.1, type: 'sine', gain: 0.1, pan });
      s.tone({ freq: 3300, dur: 0.08, type: 'sine', gain: 0.06, pan, at: 0.05 });
      break;
    case 'frozen':
      s.chord([1760, 2640, 3520], { dur: 0.5, type: 'sine', gain: 0.1, pan, release: 0.4, reverb: 0.4 });
      break;
    case 'shocked':
      s.noise({ dur: 0.06, filter: 'highpass', freq: 2500, gain: 0.2, pan });
      s.tone({ freq: 180, dur: 0.08, type: 'sawtooth', gain: 0.08, tremoloHz: 60, tremoloDepth: 0.9, pan });
      break;
    case 'knockdown':
      s.noise({ dur: 0.15, filter: 'lowpass', freq: 200, gain: 0.5, pan });
      s.tone({ freq: 80, glideTo: 40, dur: 0.15, type: 'sine', gain: 0.3, pan });
      break;
    case 'hasted':
      s.tone({ freq: 400, glideTo: 1600, dur: 0.25, type: 'triangle', gain: 0.12, pan });
      break;
    case 'slowed':
      s.tone({ freq: 800, glideTo: 200, dur: 0.4, type: 'sine', gain: 0.12, pan });
      break;
  }
}

// ------------------------------------------------------------------ big moments

export type ExplosionKind = Extract<GameEvent, { e: 'explosion' }>['kind'];

export function explosionSound(s: Synth, kind: ExplosionKind, r: number, pan: number): void {
  const size = Math.max(0.5, Math.min(2, r / 2.5));
  const dur = kind === 'beamcross' || kind === 'vortex' ? 0.8 : kind === 'mine' ? 0.3 : 0.5;
  const low = kind === 'bomb' || kind === 'meteor' || kind === 'slam';
  s.noise({ dur: dur * size, filter: 'lowpass', freq: low ? 1500 : 3000, freqTo: 80, gain: 0.6, pan, reverb: 0.6, release: 0.3 });
  s.tone({ freq: low ? 90 : 120, glideTo: 30, dur: 0.4 * size, type: 'sine', gain: 0.4, pan, reverb: 0.3 });
  if (kind === 'beamcross' || kind === 'vortex') s.chord([440, 660, 880], { dur: 0.3, type: 'sawtooth', gain: 0.08, lowpass: 2000, pan, reverb: 0.5 });
  if (kind === 'frost') s.chord([1760, 2640, 3520], { dur: 0.5, type: 'sine', gain: 0.1, pan, release: 0.4, reverb: 0.4 });
}

/** The boss winds up (a rising growl) or enrages (a long falling roar). */
export function bossSound(s: Synth, act: 'tell' | 'rage', pan: number): void {
  if (act === 'tell') {
    s.tone({ freq: 55, glideTo: 90, dur: 0.6, type: 'sawtooth', gain: 0.18, lowpass: 500, tremoloHz: 9, tremoloDepth: 0.5, pan });
    return;
  }
  s.tone({ freq: 120, glideTo: 45, dur: 1.4, type: 'sawtooth', gain: 0.25, lowpass: 700, tremoloHz: 11, tremoloDepth: 0.6, pan, reverb: 0.5 });
  s.noise({ dur: 1.0, filter: 'lowpass', freq: 400, freqTo: 60, gain: 0.5, pan, reverb: 0.5 });
}

export function mergeSound(s: Synth, pan: number): void {
  s.chord([660, 990], { dur: 0.35, type: 'sine', gain: 0.14, pan, release: 0.25, reverb: 0.5 });
}

const MAGICK_ROOT: Readonly<Record<MagickId, number>> = {
  revive: 523, haste: 659, teleport: 784, thunderbolt: 440, rain: 392, nullify: 349, timewarp: 294, conflagration: 466, phoenix: 587, blizzard: 698,
  meteor: 233, tornado: 415, thunderstorm: 370, vortex: 311, elemental: 262, stoptime: 277,
};

/** Rising arpeggio on a root per magick (DESIGN 6.8 "magick = rising arpeggio"). */
export function magickSound(s: Synth, id: MagickId, pan: number): void {
  const root = MAGICK_ROOT[id];
  [1, 1.25, 1.5, 2].forEach((ratio, i) => s.tone({ freq: root * ratio, dur: 0.25, type: 'triangle', gain: 0.14, pan, at: i * 0.07, release: 0.2, reverb: 0.4 }));
}

export function deathSound(s: Synth, player: boolean, pan: number): void {
  if (player) {
    s.noise({ dur: 1.0, filter: 'lowpass', freq: 1500, freqTo: 60, gain: 0.5, pan, reverb: 0.5, release: 0.4 });
    s.tone({ freq: 220, glideTo: 40, dur: 1.0, type: 'sawtooth', gain: 0.22, lowpass: 1200, pan, reverb: 0.4, release: 0.4 });
  } else {
    s.noise({ dur: 0.3, filter: 'bandpass', freq: 900, freqTo: 150, q: 1, gain: 0.3, pan });
    s.tone({ freq: 300, glideTo: 90, dur: 0.25, type: 'triangle', gain: 0.15, pan });
  }
}

export function reviveSound(s: Synth, pan: number): void {
  [880, 1108, 1318, 1760].forEach((freq, i) => s.tone({ freq, dur: 0.4, type: 'sine', gain: 0.14, pan, at: i * 0.06, release: 0.35, reverb: 0.5 }));
}

export function waveSound(s: Synth, phase: RoomPhase): void {
  switch (phase) {
    case 'wave':
      s.noise({ dur: 0.3, filter: 'lowpass', freq: 150, gain: 0.8, release: 0.2 });
      s.tone({ freq: 90, glideTo: 40, dur: 0.35, type: 'sine', gain: 0.6, release: 0.2 });
      s.noise({ dur: 0.2, filter: 'lowpass', freq: 120, gain: 0.5, at: 0.22 });
      s.tone({ freq: 70, glideTo: 35, dur: 0.3, type: 'sine', gain: 0.4, at: 0.22 });
      break;
    case 'breather':
      s.chord([196, 294, 392], { dur: 1.2, type: 'sine', gain: 0.12, release: 0.8, reverb: 0.6 });
      break;
    case 'countdown':
      s.tone({ freq: 1000, dur: 0.05, type: 'square', gain: 0.08 });
      break;
    case 'gameover':
      s.tone({ freq: 55, dur: 1.5, type: 'sawtooth', gain: 0.2, lowpass: 300, release: 0.8, reverb: 0.5 });
      s.tone({ freq: 440, glideTo: 110, dur: 1.2, type: 'triangle', gain: 0.12, release: 0.6 });
      break;
    case 'lobby':
      s.chord([523, 659], { dur: 0.3, type: 'sine', gain: 0.08, release: 0.3 });
      break;
  }
}

export function boltSound(s: Synth, pan: number): void {
  s.noise({ dur: 0.05, filter: 'highpass', freq: 2000, gain: 0.6, pan });
  s.noise({ dur: 0.9, filter: 'lowpass', freq: 6000, freqTo: 100, gain: 0.8, pan, reverb: 0.7, release: 0.4, at: 0.03 });
}

/** Friendly fire by the local player: two notes down, the audible "oops". */
export function oopsSound(s: Synth): void {
  s.tone({ freq: 520, dur: 0.12, type: 'triangle', gain: 0.1 });
  s.tone({ freq: 390, dur: 0.14, type: 'triangle', gain: 0.1, at: 0.12 });
}
